import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import React from 'react';
import tw from 'tailwind.macro';
import Button from '../components/button';
import Layout from '../components/layout';
import LeadText from '../components/leadText';
import Section from '../components/section';
import SEO from '../components/seo';
import PageNavigator from '../components/pageNavigator';
import SafeLink from '../components/safeLink';

const registrationLink =
  'https://www.quicket.co.za/events/385724-8th-annual-social-justice-summit/';

const Summit2026 = ({ data, location }) => {
  const parentTitle = 'The Centre for Social Justice presents:';
  const pageTitle = 'The 8th Social Justice Summit';

  return (
    <Layout location={location} parentTitle={parentTitle} pageTitle={pageTitle}>
      <SEO title={pageTitle} image={data.hero.childImageSharp.fluid.src} />
      <div css={tw`relative bg-maroon`}>
        <Img
          fluid={data.masthead.childImageSharp.fluid}
          alt="The 8th Social Justice Summit, Stellenbosch University"
          css={tw`w-full`}
        />
        <div className="sr-only">
          <LeadText>{parentTitle}</LeadText>
          <h1>{pageTitle}</h1>
        </div>
      </div>
      <PageNavigator
        items={{
          welcome: 'Welcome',
          concept: 'Concept',
          registration: 'Registration',
          previous: 'Previous Summits',
          contact: 'Contact',
        }}
        buttonText="Register now"
        buttonLink={registrationLink}
      />
      <Section id="welcome">
        <Img
          fluid={data.summit2026Invite.childImageSharp.fluid}
          alt="The 8th Social Justice Summit, 12 & 13 October 2026, Coetzenburg Centre, Stellenbosch University: The District Development Model / Inter-Governmental Relations as Catalysts for Achieving SDGs and Social Justice"
          css={tw`mb-10 lg:mb-20 w-full`}
        />
        <h2>Welcome!</h2>
        <h3>
          The theme of the summit is{' '}
          <em>
            The District Development Model / Inter-Governmental Relations as
            Catalysts for Achieving SDGs and Social Justice.
          </em>
        </h3>
        <ul>
          <li>
            <strong>Date:</strong> Monday and Tuesday, 12 &amp; 13 October 2026
          </li>
          <li>
            <strong>Time:</strong> 08:00 &ndash; 17:00 (SAST) on both days
          </li>
          <li>
            <strong>Venue:</strong> Coetzenburg Centre, Stellenbosch
            University
          </li>
          <li>
            <strong>Attendance:</strong> Free, registration required
          </li>
        </ul>
        <Button to={registrationLink} size="md" css={tw`mb-2 mr-2`}>
          Register now
        </Button>
      </Section>
      <Section id="concept" isShaded>
        <h2>Concept</h2>
        <p>
          The Centre for Social Justice (CSJ) in the Faculty of Law,
          Stellenbosch University, is delighted to invite you to attend and
          participate at the 8<sup>th</sup> Social Justice Summit. The Social
          Justice Summit is hosted by the Centre for Social Justice in
          partnership with the United Nations South Africa, the Department of
          Planning, Monitoring and Evaluation (DPME) in the Presidency, Kagiso
          Trust, Visit Stellenbosch and the South African Local Government
          Association (SALGA), among other partners.
        </p>
        <p>
          Following seven landmark iterations, the 8<sup>th</sup> Social
          Justice Summit under the theme &ldquo;The District Development Model/
          Inter-Governmental Relations as Catalysts for Achieving SDGs and
          Social Justice&rdquo; turns its focus to the institutional
          architecture of local government which will determine whether the
          country&apos;s development and social justice promise can be honoured
          at scale.
        </p>
        <p>
          The Summit will convene various stakeholders including policymakers,
          legislators, political parties, judiciary, traditional and religious
          leaders, civil society, and academia to interrogate South
          Africa&apos;s DDM/IGR as the primary institutional mechanism through
          which multiple Sustainable Development Goals (SDGs) and social justice
          can be advanced simultaneously.
        </p>
      </Section>
      <Section id="registration">
        <h2>Registration</h2>
        <LeadText>
          Attendance is free, but registration is required. Register via
          Quicket to secure your place.
        </LeadText>
        <Button to={registrationLink} size="lg" css={tw`mb-2 mr-2`}>
          Register now
        </Button>
      </Section>
      <Section id="previous" isShaded>
        <h2>Previous Summits</h2>
        <LeadText>
          You can watch free recordings of previous summits on our website.
          <ul>
            <li>
              <SafeLink
                to="/summit-2020/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2020 summit &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/summit-conference-2021/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2021 summit and conference &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/conference-summit-2022/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2022 summit and conference &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/conference-summit-2023/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2023 summit and conference &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/summit-2024/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2024 summit &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/summit-2025/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2025 summit &rarr;
              </SafeLink>
            </li>
          </ul>
        </LeadText>
      </Section>
      <Section id="contact">
        <h2>Contact</h2>
        <LeadText>
          Please direct any enquiries to the Centre for Social Justice, Faculty
          of Law, Stellenbosch University at{' '}
          <SafeLink
            to="mailto:socialjustice@sun.ac.za"
            css={tw`border-b-2 border-maroon hover:text-maroon`}
          >
            socialjustice@sun.ac.za
          </SafeLink>{' '}
          or via the contact form below.
        </LeadText>
        <form action="https://usebasin.com/f/44697ef058ef" method="POST">
          <label className="block mb-5">
            <span className="text-dark">Name:</span>
            <input
              className="form-input mt-1 block w-full"
              name="name"
              placeholder="John Smith"
            />
          </label>
          <label className="block mb-5">
            <span className="text-dark">Email:</span>
            <input
              className="form-input mt-1 block w-full"
              name="email"
              placeholder="john@smith.com"
            />
          </label>
          <label htmlFor="phone" css={tw`mb-5`}>
            <span css={tw`block`}>Phone:</span>
            <input
              name="phone"
              type="text"
              placeholder="+27 72 123 4567"
              className="form-input mt-1 block w-full"
            />
          </label>
          <label className="block mb-5">
            <span className="text-dark">Message:</span>
            <textarea
              className="form-textarea mt-1 block w-full"
              rows="3"
              name="message"
              placeholder="Your message"
            />
          </label>
          <Button type="submit" size="md">
            Submit &rarr;
          </Button>
        </form>
      </Section>
    </Layout>
  );
};

export default Summit2026;

export const pageQuery = graphql`
  query {
    hero: file(relativePath: { eq: "2026-10-summit-invite.jpg" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    masthead: file(relativePath: { eq: "2026-10-summit-masthead.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 2400, quality: 90) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    summit2026Invite: file(relativePath: { eq: "2026-10-summit-invite.jpg" }) {
      childImageSharp {
        fluid(quality: 100) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`;
